import { Temporal } from 'temporal-polyfill';
import { DayOfWeek, DayOfWeekLabelMap, SortedDaysOfWeek } from '../data/meeting-block.js';

type DaysOfWeekToNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export const DaysOfWeek = {
  /**
   * Convert a ZonedDateTime to a DayOfWeek.
   * @param zdt
   * @returns
   */
  fromZonedDateTime(zdt: Temporal.ZonedDateTime): DayOfWeek {
    return SortedDaysOfWeek[zdt.toPlainDate().dayOfWeek - 1];
  },

  /**
   * Convert a PlainDate to a DayOfWeek.
   * @param date
   * @returns
   */
  fromPlainDate(date: Temporal.PlainDate): DayOfWeek {
    return SortedDaysOfWeek[date.dayOfWeek - 1];
  },

  /**
   * Convert an Instant to a DayOfWeek.
   * @param instant
   * @param timeZone
   * @returns
   */
  fromInstant(instant: Temporal.Instant, timeZone: string): DayOfWeek {
    return this.fromZonedDateTime(instant.toZonedDateTimeISO(timeZone));
  },

  /**
   * Convert a DayOfWeek to a number.
   * @param day
   * @returns
   */
  toNumber(day: DayOfWeek): DaysOfWeekToNumber {
    return DayOfWeekLabelMap[day].day as DaysOfWeekToNumber;
  },

  /**
   * Convert a number to a DayOfWeek.
   * @param n
   * @returns
   */
  fromNumber(n: number): DayOfWeek {
    const idx = (((n - 1) % 7) + 7) % 7;
    return SortedDaysOfWeek[idx];
  },

  /**
   * Get the next DayOfWeek.
   * @param day
   * @returns
   */
  next(day: DayOfWeek): DayOfWeek {
    const i = SortedDaysOfWeek.indexOf(day);
    return SortedDaysOfWeek[(i + 1) % 7];
  },

  /**
   * Get the previous DayOfWeek.
   * @param day
   * @returns
   */
  prev(day: DayOfWeek): DayOfWeek {
    const i = SortedDaysOfWeek.indexOf(day);
    return SortedDaysOfWeek[(i + 6) % 7];
  },

  /**
   * Check if a DayOfWeek is a weekend.
   * @param day
   * @returns
   */
  isWeekend(day: DayOfWeek): boolean {
    return day === DayOfWeek.saturday || day === DayOfWeek.sunday;
  },

  /**
   * Check if a DayOfWeek is a weekday.
   * @param day
   * @returns
   */
  isWeekday(day: DayOfWeek): boolean {
    return !this.isWeekend(day);
  },

  /**
   * Compare two DayOfWeek values.
   * @param start
   * @param end
   * @returns
   */
  compare(start: DayOfWeek, end: DayOfWeek): number {
    return SortedDaysOfWeek.indexOf(start) - SortedDaysOfWeek.indexOf(end);
  },

  /**
   * Get the DayOfWeek after adding an offset.
   * @param day
   * @param offset
   * @returns
   */
  add(day: DayOfWeek, offset: number): DayOfWeek {
    const i = SortedDaysOfWeek.indexOf(day);
    const j = (((i + offset) % 7) + 7) % 7;
    return SortedDaysOfWeek[j];
  },

  /**
   * Add business days to a date. Only accounts for weekends, does not account for holidays
   * @param date
   * @param days
   * @returns
   */
  addBusinessDays(date: Temporal.PlainDate, days: number): Temporal.PlainDate {
    let finalDate = date;
    let added = 0;

    while (added < days) {
      finalDate = finalDate.add({ days: 1 });
      if (finalDate.dayOfWeek <= 5) {
        added++;
      }
    }

    return finalDate;
  },
};
